import { categoryLabels } from "@/lib/categoryLabels";
import type { CategoryStructure } from "@/lib/categoryStructure";
import type { CategorySelection, ItemCategory } from "@/lib/types";

/** Soft nudge always targets Sofa in v1 (L-shaped taxonomy is a sibling packet). */
export const CHAIR_SOFA_NUDGE_TARGET_SUBCATEGORY_ID = "sofa-couche" as const;

/**
 * Sofa/couch family words in titles (case-insensitive).
 * Keep aligned with chair-sofa-sell-clarity Phase 0 D5.
 */
const SOFA_LIKE_TITLE_PATTERN =
  /sofa|couch|couche|settee|loveseat|love\s*seat|sectional/i;

export function titleLooksLikeSofaOrCouch(title: string): boolean {
  return SOFA_LIKE_TITLE_PATTERN.test(title.trim());
}

/**
 * Soft Chair → Sofa nudge: only when Chair is selected and the title looks like a sofa/couch.
 * Never a hard block — callers must keep dismiss / Keep Chair paths.
 */
export function shouldNudgeChairTowardSofa(
  title: string | null | undefined,
  subcategoryId: string | null | undefined,
): boolean {
  if (subcategoryId !== "chair") return false;
  if (!title?.trim()) return false;
  return titleLooksLikeSofaOrCouch(title);
}

/**
 * Builds a CategorySelection for Sofa from the active sell structure.
 * Returns null if Sofa is not available (inactive / missing).
 */
export function resolveSofaCategorySelection(
  categoryStructure: CategoryStructure[],
): CategorySelection | null {
  for (const category of categoryStructure) {
    const sub = category.subcategories.find(
      (s) => s.id === CHAIR_SOFA_NUDGE_TARGET_SUBCATEGORY_ID,
    );
    if (sub) {
      const categoryId = category.id as ItemCategory;
      return {
        categoryId,
        subcategoryId: CHAIR_SOFA_NUDGE_TARGET_SUBCATEGORY_ID,
        categoryName: categoryLabels[categoryId] || category.name,
        subcategoryName: sub.name,
      };
    }
  }
  return null;
}
